import { http } from '@/api/http'
import { tokenStorage } from '@/api/tokenStorage'
import type { PrincipalVO, SignInVO } from '@/types/api'

/**
 * Sign-in for internal staff, via a Google ID token.
 *
 *   POST /api/v1/auth/google   { "idToken": "<Google ID token>" }
 *   ->  ResponseBodyEntity<SignInVO>
 *
 * The backend verifies the token's signature, issuer and audience against Google's keys, then matches
 * it to an operator on the Google `sub` (never the email — an address can be reassigned) and issues the
 * same dual tokens as password sign-in. An identity with no bound operator is rejected, not
 * auto-provisioned: accounts exist only in the database.
 */
export function signInWithGoogle(idToken: string): Promise<SignInVO> {
  return http.post<SignInVO>('/api/v1/auth/google', { idToken })
}

/**
 * Sign-in for suppliers, with a username and password.
 *
 * Deliberately the secondary path: internal staff use Google, and this exists for factory accounts
 * that cannot.
 *
 * `client` tells the backend which program is asking, so an account that may not use the console — a
 * station account typed into the wrong window — is refused here with `auth.client.not_allowed` instead of
 * signing in to an empty navigation. Sent explicitly rather than relying on the backend's default, so the
 * one door this file opens is named at the call site. The station tools send their own value; the same
 * endpoint serves all of them.
 */
export function signInWithPassword(
  username: string,
  password: string,
  rememberMe: boolean
): Promise<SignInVO> {
  return http.post<SignInVO>('/api/v1/auth/login', {
    username,
    password,
    rememberMe,
    client: 'WEB_CONSOLE'
  })
}

export function fetchCurrentOperator(): Promise<PrincipalVO> {
  return http.get<PrincipalVO>('/api/v1/auth/me')
}

/**
 * Revokes the refresh token server-side. Local state is cleared by the caller regardless of whether
 * this succeeds — a failed sign-out must still sign the user out of this browser.
 */
export function signOut(): Promise<void> {
  const refreshToken = tokenStorage.getRefreshToken()
  return http.post<void>('/api/v1/auth/logout', null, {
    headers: refreshToken ? { 'X-Refresh-Token': refreshToken } : {}
  })
}
